import Icon from "@mui/material/Icon";
import Link from "@mui/material/Link";
import { styled } from "@mui/material";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import ViewCompanyProfile from "marketplace/companies/profiles/_views/view-company-profile";
import TeamTemplate from "work/team-templates/team-template";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import { useMemo } from "react";
import Individual from "marketplace/individuals/individual";
import { skipToken } from "@reduxjs/toolkit/query";

const TeamColumnLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  display: "inline-block",
  textDecorationColor: theme.palette.common.black,
  cursor: "pointer",
  "&:hover": {
    textDecoration: "underline",
  },
}));

type CompanyColumnCellProps = {
  teamTemplate?: TeamTemplate;
  teamUserIds?: Guid[];
  activeTeam?: VendorTeam;
};

export default function CompanyColumnCell(props: Readonly<CompanyColumnCellProps>) {
  const { teamTemplate, teamUserIds, activeTeam } = props;
  const { openDialog, closeDialog } = useDialog();

  let leaderUserId: Guid | undefined;
  let individualsUserIds: Guid[] = [];

  if (activeTeam) {
    leaderUserId = activeTeam.leader?.userId;
    individualsUserIds = [
      ...(activeTeam.members?.map((m) => m.userId) ?? []),
      ...(activeTeam.leader?.userId ? [activeTeam.leader.userId] : []),
    ].filter(Boolean);
  } else if (teamTemplate) {
    leaderUserId = teamTemplate.leader?.userId;
    individualsUserIds = teamTemplate.members?.map((m) => m.userId) ?? [];
    if (teamTemplate.leader?.userId) individualsUserIds.push(teamTemplate.leader.userId);
  } else if (teamUserIds) {
    individualsUserIds = teamUserIds;
  }

  const individualsQuery = useGetIndividualsByUserIdsQuery(
    individualsUserIds.length ? { userIds: new Set(individualsUserIds.map((id) => id.value)) } : skipToken,
  );

  const leaderIndividual = useMemo(() => {
    if (!leaderUserId || !individualsQuery.data) return undefined;
    const individuals = Individual.fromJSONArray(individualsQuery.data) ?? [];
    const leader = individuals.find((individual) => individual.userId.isEqualTo(leaderUserId));
    return leader;
  }, [individualsQuery.data, leaderUserId]);

  if (!leaderIndividual?.company?.profile) {
    return <Icon>remove</Icon>;
  }

  return (
    <TeamColumnLink
      key={leaderIndividual.company.id.value}
      onClick={(event) => {
        event.stopPropagation();
        openDialog({
          component: <ViewCompanyProfile companyId={leaderIndividual.company.id} />,
          contentSxProps: {
            display: "flex",
          },
          MuiProps: {
            fullWidth: true,
            maxWidth: "lg",
          },
          titleStyle: {
            paddingBottom: 0,
          },
          onClose: closeDialog,
        });
      }}
    >
      {leaderIndividual.company.profile.name}
    </TeamColumnLink>
  );
}
